import type React from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type { MostVisitedCompany } from '@xing-com/crate-common-graphql-types';

import { SubpageLayout } from '../../components/layouts/subpage-layout';
import { NewPagination } from '../../components/new-pagination/new-pagination';
import { SimpleCompanyCardGrid } from '../../components/simple-company-card-grid/simple-company-card-grid';
import { useMostVisitedCompanies } from '../../hooks/use-most-visited-companies';
import { usePagination } from '../../hooks/use-pagination';
import { useTrackPage } from '../../hooks/use-track-page';
import { createSimpleCompanies } from '../../models/most-visited-companies';
import type { SimpleCompanyItem } from '../../types';
import { pagesTrackingName } from '../../utils/tracking';

const LIMIT = 24;

export const NewMostVisitedCompanies: React.FC = () => {
  const intl = useIntl();
  const { changePage, activePage } = usePagination();
  const [totalCompanies, setTotalCompanies] = useState(0);

  const { data, loading } = useMostVisitedCompanies({
    limit: LIMIT,
    offset: LIMIT * (activePage - 1),
  });

  useEffect(() => {
    const total = data?.mostVisitedCompanies?.total ?? 0;

    if (!loading && totalCompanies !== total) {
      setTotalCompanies(total);
    }
  }, [loading]);

  let mostVisitedCompanies: SimpleCompanyItem[] = [];

  if (data?.mostVisitedCompanies?.collection) {
    const companies = data.mostVisitedCompanies
      .collection as MostVisitedCompany[];
    mostVisitedCompanies = createSimpleCompanies(companies);
  }

  useTrackPage({ pageName: pagesTrackingName.mostVisited });

  return (
    <SubpageLayout
      headerText={intl.$t({ id: 'CP_100_MOST_CLICKED_COMPANIES' })}
      description={intl.$t({ id: 'CP_100_MOST_CLICKED_COMPANIES_TEXT' })}
    >
      {mostVisitedCompanies && (
        <SimpleCompanyCardGrid
          items={mostVisitedCompanies}
          limit={LIMIT}
          loading={loading}
        />
      )}
      {mostVisitedCompanies && (
        <NewPagination
          currentPage={activePage}
          perPage={LIMIT}
          total={totalCompanies}
          onPageClick={changePage}
        />
      )}
    </SubpageLayout>
  );
};
